import React from 'react';
import { TagType, TemplateType } from '@dev/ee-api/ee-api-models';
import { push as Push, replace as Replace } from 'redux-first-history';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { stringify } from 'query-string';
import { TemplateList as TemplateListModule } from '@dev/ee-modules';

import { TemplatesPaths } from '@/services/dictionaries/Paths';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { getTemplateTagsList, templateTagListStart } from '@/store/templateTagList/actions';
import {
    getTemplateList,
    SetTemplateListName,
    StartLoadingTemplateParams,
    StartLoadingTemplates,
    TemplatePreviewStart,
} from '@/store/templateList/actions';
import { getRawEditorState } from '@/store/raweditor/actions';
import { getConfigStore } from '@/store';
import { addParamToUrl } from '@/services/tools/handleUrlParams';
import { getAccountStore } from '@/store/account/actions';
import { GetDashboardSettings, SetDefaultEditor, getDashboardSettingsStore } from '@/store/dashboardSettings/actions';
import { ApplicationState } from '@/store';
import { EditCampaignTemplate, OpenPreviewOverlay, SelectTemplate } from '@/store/campaignCreator/actions';

export const GalleryTemplateList = (): JSX.Element => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();

    const config = useSelector(getConfigStore);
    const account = useSelector(getAccountStore);
    const browser = useSelector((state: ApplicationState) => state.browser);
    const templates = useSelector(getTemplateList);
    const tags = useSelector(getTemplateTagsList);
    const rawEditor = useSelector(getRawEditorState);
    const dashboardSettings = useSelector(getDashboardSettingsStore);

    const startLoadingTemplates = (params: StartLoadingTemplateParams) => dispatch(StartLoadingTemplates(params));
    const tagListStart = (type: TagType) => dispatch(templateTagListStart(type));
    const showDialog = (params: ShowDialogParams) => dispatch(ShowDialog(params));
    const selectTemplate = (templateName: string, isPublic?: boolean) =>
        dispatch(SelectTemplate(templateName, isPublic, true));
    const push = (url: string) => dispatch(Push(url));
    const templatePreviewStart = (templateName: string, isPublic?: boolean) =>
        dispatch(TemplatePreviewStart(templateName, isPublic));
    const editTemplateFromGallery = (templateName: string, templateType: TemplateType, isPublic?: boolean) => {
        const urlQueryParams = { isPublic };
        dispatch(EditCampaignTemplate(templateName, templateType, isPublic, true));
        dispatch(Replace({ search: stringify(urlQueryParams as any) }));
    };
    const previewTemplateFromGallery = (templateName: string) => {
        dispatch(SetTemplateListName(templateName));
        dispatch(OpenPreviewOverlay());
    };

    const setUrlParams = (param: Record<string, any>): void => {
        const urlParams = addParamToUrl(param);
        dispatch(Replace({ search: urlParams }));
    };

    // scroll container have to be added as query selector string (DOM node mentioned in react-lazyload documentation doesn't work)
    const galleryScrollContainerId = 'galleryScrollContainer';
    const galleryScrollContainerSelector = `#${galleryScrollContainerId} .MuiDialog-paperScrollBody`;

    return (
        <TemplateListModule
            paths={TemplatesPaths}
            t={t}
            config={config}
            account={account}
            dashboardSettings={dashboardSettings}
            browser={browser}
            templates={templates}
            tags={tags}
            raweditor={rawEditor}
            retriveUrlParamsToAdd={setUrlParams}
            location={location}
            isCampaignCreator
            isMarketing
            APIPATH={APIPATH}
            GalleryTemplatePreview={previewTemplateFromGallery}
            GalleryTemplateEdit={editTemplateFromGallery}
            StartLoadingTemplates={startLoadingTemplates}
            templateTagListStart={tagListStart}
            ShowDialog={showDialog}
            SelectTemplate={selectTemplate}
            GetDashboardSettings={GetDashboardSettings}
            SetDefaultEditor={SetDefaultEditor}
            Push={push}
            TemplatePreviewStart={templatePreviewStart}
            // scroll container have to be added as query selector string (DOM node mentioned in react-lazyload documentation doesn't work)
            lazyLoadScrollContainer={galleryScrollContainerSelector}
        />
    );
};
